import styled from "styled-components";
import dayjs from "dayjs";
import moment from "moment-timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useState, useRef, useEffect } from "react";
import { Col, DatePicker, Row, TimePicker } from "antd";
import { RATypography, RAButtonSecondary, BUTTON_SIZES } from "../customs";
import { RAInputTimePickerMobile } from "@/components/atoms";

import { SvgArrowLeft, SvgArrowRight, SvgCalendar, SvgSeparator, SvgTime } from "@/assets/icons";
import { DATE_FORMAT_DEPARTURE_SHORT } from "@/config/constants";
import { useSelector } from "react-redux";
import { isFormatTime12H } from "@/stores/slices/authSlice";
dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const dateFormat = "ddd, MMM DD - HH:mm";
const dateFormat12H = "ddd, MMM DD - hh:mm A";

export const RADepartureWindow = ({ fromDate, toDate, onChange, title, disabledFromDate, error }) => {
	const inputRef = useRef(null);
	const containerRef = useRef(null);
	const panelRef = useRef(null);
	const fromTimeRef = useRef(null);
	const toTimeRef = useRef(null);

	const isTime12H = useSelector(isFormatTime12H);
	const [openPanel, setOpenPanel] = useState(false);

	const disabledDate = (current) => {
		// Can not select days before today and today
		if (disabledFromDate) {
			return current && current < dayjs(disabledFromDate).add(-1, "days").endOf("day");
		}
		return current && current < dayjs().add(-1, "days").endOf("day");
	};

	const onChangeRangeDate = (values = []) => {
		let from = values[0].toISOString();
		let to = values[1].toISOString();
		onChange(from, to);
	};

	const onChangeTimeFromDate = (val) => {
		let date = moment(fromDate);
		let time = moment(val);
		date.set({
			hour: time.get("hour"),
			minute: time.get("minute"),
			second: time.get("second"),
		});
		onChange(date.toISOString(), toDate);
	};

	const onChangeTimeToDate = (val) => {
		let date = moment(toDate);
		let time = moment(val);
		date.set({
			hour: time.get("hour"),
			minute: time.get("minute"),
			second: time.get("second"),
		});
		onChange(fromDate, date.toISOString());
	};

	useEffect(() => {
		const handler = (e) => {
			if (
				containerRef.current &&
				!containerRef.current.contains?.(e.target) &&
				panelRef.current &&
				!panelRef.current.contains?.(e.target) &&
				(!fromTimeRef.current || (fromTimeRef.current && !fromTimeRef.current.contains?.(e.target))) &&
				(!toTimeRef.current || (toTimeRef.current && !toTimeRef.current.contains?.(e.target)))
			) {
				setOpenPanel(false);
			}
		};

		document.addEventListener("mousedown", handler);
		document.addEventListener("touchstart", handler);

		return () => {
			document.removeEventListener("mousedown", handler);
			document.removeEventListener("touchstart", handler);
		};
	}, []);

	const renderExtraFooter = () => {
		return (
			<ExtraFooter
				inputRef={inputRef}
				setOpen={setOpenPanel}
				fromDate={fromDate}
				toDate={toDate}
				onChangeTimeFromDate={onChangeTimeFromDate}
				onChangeTimeToDate={onChangeTimeToDate}
				isTime12H={isTime12H}
			/>
		);
	};

	return (
		<WrapperDepartureWindow ref={containerRef}>
			{title && (
				<RATypography color="#595959" variant="sub-heading4" bottom={8}>
					{title}
				</RATypography>
			)}
			<RangePicker
				ref={inputRef}
				open={openPanel}
				value={fromDate ? [dayjs(fromDate), dayjs(toDate)] : null}
				format={!isTime12H ? dateFormat : dateFormat12H}
				allowClear={false}
				suffixIcon={null}
				style={{ width: "100%", height: "52px" }}
				panelRender={(originPanel) => <PanelWrapper ref={panelRef}>{originPanel}</PanelWrapper>}
				separator={<SvgSeparator />}
				disabledDate={disabledDate}
				showNow={false}
				superNextIcon={null}
				superPrevIcon={null}
				onChange={onChangeRangeDate}
				nextIcon={<SvgArrowRight />}
				prevIcon={<SvgArrowLeft />}
				renderExtraFooter={renderExtraFooter}
				inputReadOnly
				inputRender={(props) => (
					<InputRenderWrapper>
						<SvgCalendar />
						<InputRender {...props} />
					</InputRenderWrapper>
				)}
				onOpenChange={(o) => o && setOpenPanel(o)}
			/>
			{!!error && (
				<RATypography variant="body3" color="#BF1A2F" top="4px">
					{error}
				</RATypography>
			)}
		</WrapperDepartureWindow>
	);
};

const ExtraFooter = ({ inputRef, setOpen, fromDate, toDate, onChangeTimeFromDate, onChangeTimeToDate, isTime12H }) => {
	const [isTouchScreen, setIsTouchScreen] = useState(false);

	useEffect(() => {
		const inputContainer = inputRef.current;
		if (inputContainer) {
			inputContainer.blur();
		}
		if ("ontouchstart" in window || navigator.msMaxTouchPoints > 0 || navigator.maxTouchPoints > 0) {
			setIsTouchScreen(true);
		}
	}, []);

	return (
		<Row
			style={{
				padding: "10px 32px 20px 32px",
				width: "100%",
			}}
			onMouseDown={(e) => e.stopPropagation()}
			align="bottom"
		>
			<Col span={10}>
				<RATypography variant="body4">From</RATypography>
				<Row gutter={[12, 12]} align="middle">
					<Col>
						<RATypography variant="sub-heading4">
							{fromDate ? moment(fromDate).format(DATE_FORMAT_DEPARTURE_SHORT) : "-- --"}
						</RATypography>
					</Col>
					<Col>
						{isTouchScreen ? (
							<>
								<RAInputTimePickerMobile
									prefix={<SvgTime color="#595959" />}
									placeholder="Time"
									// value={departure_datetime || null}
									disabled={!fromDate}
									onChange={(val) => {
										onChangeTimeFromDate(val);
									}}
								/>
							</>
						) : (
							<>
								<TimePicker
									suffixIcon={null}
									disabled={!fromDate}
									showNow={false}
									format={!isTime12H ? "HH:mm" : "hh:mm A"}
									minuteStep={5}
									value={fromDate ? dayjs(fromDate) : null}
									onSelect={(val) => onChangeTimeFromDate(val.toISOString())}
									inputRender={(props) => {
										return (
											<InputRenderWrapper>
												<div style={{ display: "flex" }}>
													<SvgTime color="#595959" />
												</div>
												<InputRender {...props} />
											</InputRenderWrapper>
										);
									}}
								/>
							</>
						)}
					</Col>
				</Row>
			</Col>
			<Col span={10}>
				<RATypography variant="body4">To</RATypography>
				<Row gutter={[12, 12]} align="middle">
					<Col>
						<RATypography variant="sub-heading4">
							{toDate ? moment(toDate).format(DATE_FORMAT_DEPARTURE_SHORT) : "-- --"}
						</RATypography>
					</Col>
					<Col>
						{isTouchScreen ? (
							<>
								<RAInputTimePickerMobile
									prefix={<SvgTime color="#595959" />}
									placeholder="Time"
									// value={departure_datetime || null}
									disabled={!fromDate}
									onChange={(val) => {
										onChangeTimeToDate(val);
									}}
								/>
							</>
						) : (
							<>
								<TimePicker
									suffixIcon={null}
									disabled={!fromDate}
									format={!isTime12H ? "HH:mm" : "hh:mm A"}
									showNow={false}
									value={toDate ? dayjs(toDate) : null}
									minuteStep={5}
									onSelect={(val) => onChangeTimeToDate(val.toISOString())}
									inputRender={(props) => {
										return (
											<InputRenderWrapper>
												<div style={{ display: "flex" }}>
													<SvgTime color="#595959" />
												</div>
												<InputRender {...props} />
											</InputRenderWrapper>
										);
									}}
								/>
							</>
						)}
					</Col>
				</Row>
			</Col>
			<Col span={4}>
				<RAButtonSecondary size={BUTTON_SIZES.medium} title="APPLY" onClick={() => setOpen(false)} />
			</Col>
		</Row>
	);
};

const InputRenderWrapper = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	height: 30px;
`;

const InputRender = styled.input`
	border: 0;
	outline: 0;
	font-size: 14px;
	line-height: 24px;
	color: #222222;
	margin-left: 12px;
	width: 100%;
	height: 100%;
	padding: 0;
`;

const PanelWrapper = styled.div`
	.ant-picker-date-panel {
		width: auto;
	}
	.ant-picker-header {
		padding: 0 24px;
		margin-top: 16px;
		border: 0;
	}
	.ant-picker-date-panel .ant-picker-body {
		padding: 8px 24px 16px 24px;
	}
	.ant-picker-header-super-prev-btn,
	.ant-picker-header-super-next-btn {
		display: none;
	}
	.ant-picker-header-prev-btn,
	.ant-picker-header-next-btn {
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.ant-picker-header-view {
		font-size: 16px;
		line-height: 24px;
		color: #222222;
	}
	.ant-picker-content thead th {
		font-size: 12px;
		line-height: 20px;
		color: #3c3c43;
		text-transform: uppercase;
	}
	.ant-picker-date-panel .ant-picker-content th {
		width: 42px;
	}
	.ant-picker-dropdown .ant-picker-content td {
		padding: 0 !important;
	}
	.ant-picker-cell-in-view.ant-picker-cell-range-end:not(.ant-picker-cell-range-end-single)::before {
		background: rgba(255, 177, 26, 0.2);
		max-width: 21px;
		height: 42px;
		line-height: 42px;
	}
	.ant-picker-cell-in-view.ant-picker-cell-in-range::before {
		background: rgba(255, 177, 26, 0.2);
		min-width: 42px;
		height: 42px;
		line-height: 42px;
	}
	.ant-picker-cell-in-view.ant-picker-cell-range-start:not(.ant-picker-cell-range-start-single)::before {
		background: rgba(255, 177, 26, 0.2);
		max-width: 21px;
		height: 42px;
		line-height: 42px;
	}
	.ant-picker-cell .ant-picker-cell-inner {
		display: flex;
		min-width: 42px;
		height: 42px;
		line-height: 42px;
		border-radius: 42px;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		margin: 0 auto;
		border-radius: 42px !important;
	}
	.ant-picker-cell-in-view .ant-picker-cell-inner {
		color: #222222 !important;
		border-radius: 42px !important;
	}
	.ant-picker-cell-today .ant-picker-cell-inner::before {
		border-radius: 42px !important;
	}
`;

const WrapperDepartureWindow = styled.div`
	.ant-picker .ant-picker-input {
		gap: 6px;
	}
	.ant-picker .ant-picker-input span.ant-picker-suffix,
	.ant-picker.ant-picker-range span.ant-picker-suffix {
		margin-left: 1px;
		margin-right: 6px;
		order: -1;
	}
	.ant-picker-range {
		padding: 4px 20px 4px;
	}
	.ant-picker-range .ant-picker-active-bar {
		margin-inline-start: 12px;
	}
	.ant-picker-range-separator {
		padding: 0 12px;
	}
	.ant-picker-input::before {
		content: url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%20%20%20%20%3Cpath%0A%20%20%20%20%20%20%20%20d%3D%22M9.99984%2011.6667C10.1647%2011.6667%2010.3258%2011.6179%2010.4628%2011.5263C10.5999%2011.4347%2010.7067%2011.3046%2010.7697%2011.1523C10.8328%2011%2010.8493%2010.8325%2010.8172%2010.6708C10.785%2010.5092%2010.7056%2010.3607%2010.5891%2010.2442C10.4725%2010.1276%2010.3241%2010.0482%2010.1624%2010.0161C10.0008%209.98394%209.83321%2010.0004%209.68093%2010.0635C9.52866%2010.1266%209.39851%2010.2334%209.30695%2010.3704C9.21538%2010.5075%209.1665%2010.6686%209.1665%2010.8334C9.1665%2011.0544%209.2543%2011.2664%209.41058%2011.4227C9.56686%2011.579%209.77882%2011.6667%209.99984%2011.6667ZM14.1665%2011.6667C14.3313%2011.6667%2014.4924%2011.6179%2014.6295%2011.5263C14.7665%2011.4347%2014.8733%2011.3046%2014.9364%2011.1523C14.9995%2011%2015.016%2010.8325%2014.9838%2010.6708C14.9517%2010.5092%2014.8723%2010.3607%2014.7558%2010.2442C14.6392%2010.1276%2014.4907%2010.0482%2014.3291%2010.0161C14.1674%209.98394%2013.9999%2010.0004%2013.8476%2010.0635C13.6953%2010.1266%2013.5652%2010.2334%2013.4736%2010.3704C13.382%2010.5075%2013.3332%2010.6686%2013.3332%2010.8334C13.3332%2011.0544%2013.421%2011.2664%2013.5772%2011.4227C13.7335%2011.579%2013.9455%2011.6667%2014.1665%2011.6667ZM9.99984%2015.0001C10.1647%2015.0001%2010.3258%2014.9512%2010.4628%2014.8596C10.5999%2014.7681%2010.7067%2014.6379%2010.7697%2014.4857C10.8328%2014.3334%2010.8493%2014.1658%2010.8172%2014.0042C10.785%2013.8425%2010.7056%2013.694%2010.5891%2013.5775C10.4725%2013.4609%2010.3241%2013.3816%2010.1624%2013.3494C10.0008%2013.3173%209.83321%2013.3338%209.68093%2013.3968C9.52866%2013.4599%209.39851%2013.5667%209.30695%2013.7038C9.21538%2013.8408%209.1665%2014.0019%209.1665%2014.1667C9.1665%2014.3878%209.2543%2014.5997%209.41058%2014.756C9.56686%2014.9123%209.77882%2015.0001%209.99984%2015.0001ZM14.1665%2015.0001C14.3313%2015.0001%2014.4924%2014.9512%2014.6295%2014.8596C14.7665%2014.7681%2014.8733%2014.6379%2014.9364%2014.4857C14.9995%2014.3334%2015.016%2014.1658%2014.9838%2014.0042C14.9517%2013.8425%2014.8723%2013.694%2014.7558%2013.5775C14.6392%2013.4609%2014.4907%2013.3816%2014.3291%2013.3494C14.1674%2013.3173%2013.9999%2013.3338%2013.8476%2013.3968C13.6953%2013.4599%2013.5652%2013.5667%2013.4736%2013.7038C13.382%2013.8408%2013.3332%2014.0019%2013.3332%2014.1667C13.3332%2014.3878%2013.421%2014.5997%2013.5772%2014.756C13.7335%2014.9123%2013.9455%2015.0001%2014.1665%2015.0001ZM5.83317%2011.6667C5.99799%2011.6667%206.1591%2011.6179%206.29615%2011.5263C6.43319%2011.4347%206.54%2011.3046%206.60307%2011.1523C6.66614%2011%206.68265%2010.8325%206.65049%2010.6708C6.61834%2010.5092%206.53897%2010.3607%206.42243%2010.2442C6.30588%2010.1276%206.1574%2010.0482%205.99575%2010.0161C5.8341%209.98394%205.66654%2010.0004%205.51427%2010.0635C5.362%2010.1266%205.23185%2010.2334%205.14028%2010.3704C5.04871%2010.5075%204.99984%2010.6686%204.99984%2010.8334C4.99984%2011.0544%205.08764%2011.2664%205.24392%2011.4227C5.4002%2011.579%205.61216%2011.6667%205.83317%2011.6667ZM15.8332%203.33341H14.9998V2.50008C14.9998%202.27907%2014.912%202.06711%2014.7558%201.91083C14.5995%201.75455%2014.3875%201.66675%2014.1665%201.66675C13.9455%201.66675%2013.7335%201.75455%2013.5772%201.91083C13.421%202.06711%2013.3332%202.27907%2013.3332%202.50008V3.33341H6.6665V2.50008C6.6665%202.27907%206.57871%202.06711%206.42243%201.91083C6.26615%201.75455%206.05418%201.66675%205.83317%201.66675C5.61216%201.66675%205.4002%201.75455%205.24392%201.91083C5.08764%202.06711%204.99984%202.27907%204.99984%202.50008V3.33341H4.1665C3.50346%203.33341%202.86758%203.59681%202.39874%204.06565C1.9299%204.53449%201.6665%205.17037%201.6665%205.83342V15.8334C1.6665%2016.4965%201.9299%2017.1323%202.39874%2017.6012C2.86758%2018.07%203.50346%2018.3334%204.1665%2018.3334H15.8332C16.4962%2018.3334%2017.1321%2018.07%2017.6009%2017.6012C18.0698%2017.1323%2018.3332%2016.4965%2018.3332%2015.8334V5.83342C18.3332%205.17037%2018.0698%204.53449%2017.6009%204.06565C17.1321%203.59681%2016.4962%203.33341%2015.8332%203.33341ZM16.6665%2015.8334C16.6665%2016.0544%2016.5787%2016.2664%2016.4224%2016.4227C16.2661%2016.579%2016.0542%2016.6667%2015.8332%2016.6667H4.1665C3.94549%2016.6667%203.73353%2016.579%203.57725%2016.4227C3.42097%2016.2664%203.33317%2016.0544%203.33317%2015.8334V8.33342H16.6665V15.8334ZM16.6665%206.66675H3.33317V5.83342C3.33317%205.6124%203.42097%205.40044%203.57725%205.24416C3.73353%205.08788%203.94549%205.00008%204.1665%205.00008H15.8332C16.0542%205.00008%2016.2661%205.08788%2016.4224%205.24416C16.5787%205.40044%2016.6665%205.6124%2016.6665%205.83342V6.66675ZM5.83317%2015.0001C5.99799%2015.0001%206.1591%2014.9512%206.29615%2014.8596C6.43319%2014.7681%206.54%2014.6379%206.60307%2014.4857C6.66614%2014.3334%206.68265%2014.1658%206.65049%2014.0042C6.61834%2013.8425%206.53897%2013.694%206.42243%2013.5775C6.30588%2013.4609%206.1574%2013.3816%205.99575%2013.3494C5.8341%2013.3173%205.66654%2013.3338%205.51427%2013.3968C5.362%2013.4599%205.23185%2013.5667%205.14028%2013.7038C5.04871%2013.8408%204.99984%2014.0019%204.99984%2014.1667C4.99984%2014.3878%205.08764%2014.5997%205.24392%2014.756C5.4002%2014.9123%205.61216%2015.0001%205.83317%2015.0001Z%22%0A%20%20%20%20%20%20%20%20fill%3D%22%23595959%22%20%2F%3E%0A%3C%2Fsvg%3E");
		margin-right: 12px;
		margin-top: 2px;
	}
`;
